import { useEffect, useRef, useState } from 'react';
import { api, type SearchHit } from '../../lib/api.js';
import { DvIcon } from '../DvIcon.js';

/** Знаходить активну [[-згадку перед кареткою (open-індекс і запит після [[). */
function activeMention(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const open = upto.lastIndexOf('[[');
  if (open === -1) return null;
  const between = upto.slice(open + 2);
  if (between.includes(']]') || between.includes('[[') || between.includes('\n')) return null;
  return { start: open, query: between };
}

interface MentionState { start: number; caret: number; hits: SearchHit[]; idx: number; }

/**
 * Textarea з автодоповненням wiki-згадок: набери `[[` → жива пошукова панель,
 * вибір вставляє `[[slug|Назва]]`. Кнопка «＋ згадка» вставляє `[[` і відкриває пошук.
 */
export function LinkTextarea({ value, onChange, rows = 3, placeholder }: {
  value: string; onChange: (v: string) => void; rows?: number; placeholder?: string;
}): JSX.Element {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [m, setM] = useState<MentionState | null>(null);

  function refresh(text: string, caret: number): void {
    const mn = activeMention(text, caret);
    if (!mn) { setM(null); return; }
    setM((prev) => ({ start: mn.start, caret, hits: prev?.hits ?? [], idx: 0 }));
    const q = mn.query.trim();
    api.wikiSearch(q || '', 8).then((r) => {
      setM((cur) => (cur ? { ...cur, hits: r.hits } : cur));
    }).catch(() => { /* noop */ });
  }

  function insert(hit: SearchHit): void {
    if (!m) return;
    const before = value.slice(0, m.start);
    const after = value.slice(m.caret);
    const token = `[[${hit.slug}|${hit.title}]]`;
    const next = before + token + after;
    onChange(next);
    setM(null);
    const pos = before.length + token.length;
    requestAnimationFrame(() => { const el = ref.current; if (el) { el.focus(); el.setSelectionRange(pos, pos); } });
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (!m || m.hits.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setM({ ...m, idx: (m.idx + 1) % m.hits.length }); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setM({ ...m, idx: (m.idx - 1 + m.hits.length) % m.hits.length }); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insert(m.hits[m.idx]!); }
    else if (e.key === 'Escape') { e.preventDefault(); setM(null); }
  }

  function addMention(): void {
    const el = ref.current; if (!el) return;
    const caret = el.selectionStart ?? value.length;
    const next = value.slice(0, caret) + '[[' + value.slice(caret);
    onChange(next);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(caret + 2, caret + 2); refresh(next, caret + 2); } });
  }

  return (
    <div className="dv-linkta">
      <textarea
        ref={ref}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); refresh(e.target.value, e.target.selectionStart ?? e.target.value.length); }}
        onKeyDown={onKey}
        onClick={(e) => refresh(value, (e.target as HTMLTextAreaElement).selectionStart ?? 0)}
        onBlur={() => setTimeout(() => setM(null), 150)}
      />
      <button type="button" className="dv-linkta__add" onMouseDown={(e) => { e.preventDefault(); addMention(); }} title="Згадати сторінку">
        <DvIcon name="link" size={13} /> згадка
      </button>
      {m && m.hits.length > 0 && (
        <div className="dv-mention" role="listbox">
          {m.hits.map((h, i) => (
            <button key={h.slug} type="button" className={`dv-mention__item ${i === m.idx ? 'on' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); insert(h); }}>
              <span className="dv-mention__title">{h.title}</span>
              {h.category && <span className="dv-mention__cat">{h.category}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Інпут тегів: чипи з «×» + поле; Enter/кома додає, Backspace на порожньому — прибирає останній. Зберігає CSV. */
export function TagInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  const tags = value.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  const [draft, setDraft] = useState('');
  const commit = (t: string): void => { const v = t.trim(); if (v && !tags.includes(v)) onChange([...tags, v].join(', ')); setDraft(''); };
  return (
    <div className="dv-taginput">
      {tags.map((t) => (
        <span key={t} className="dv-taginput__chip">{t}<button type="button" onClick={() => onChange(tags.filter((x) => x !== t).join(', '))} aria-label="Прибрати"><DvIcon name="close" size={11} /></button></span>
      ))}
      <input
        value={draft}
        placeholder={tags.length ? '' : (placeholder ?? 'додати…')}
        onChange={(e) => { const v = e.target.value; if (v.endsWith(',')) commit(v.slice(0, -1)); else setDraft(v); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(draft); } else if (e.key === 'Backspace' && !draft && tags.length) onChange(tags.slice(0, -1).join(', ')); }}
        onBlur={() => commit(draft)}
      />
    </div>
  );
}

/** Чип-пікер повʼязаних сторінок: шукай і клікай (зберігає CSV slug'ів). */
export function RelatedPicker({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  const slugs = value.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (q.trim().length < 1) { setHits([]); return; }
    const t = setTimeout(() => {
      api.wikiSearch(q, 8).then((r) => setHits(r.hits.filter((h) => !slugs.includes(h.slug)))).catch(() => setHits([]));
    }, 180);
    return () => clearTimeout(t);
  }, [q, value]);

  function add(h: SearchHit): void {
    setLabels((l) => ({ ...l, [h.slug]: h.title }));
    onChange([...slugs, h.slug].join(', '));
    setQ('');
    setHits([]);
  }
  function remove(slug: string): void {
    onChange(slugs.filter((s) => s !== slug).join(', '));
  }

  return (
    <div className="dv-relpicker">
      {slugs.length > 0 && (
        <div className="dv-relpicker__chips">
          {slugs.map((s) => (
            <span key={s} className="dv-relchip">
              <DvIcon name="link" size={12} /> {labels[s] ?? s}
              <button type="button" onClick={() => remove(s)} aria-label="Прибрати"><DvIcon name="close" size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="dv-relpicker__search">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Шукати сторінку для звʼязку…" />
        {hits.length > 0 && (
          <div className="dv-mention" role="listbox">
            {hits.map((h) => (
              <button key={h.slug} type="button" className="dv-mention__item" onMouseDown={(e) => { e.preventDefault(); add(h); }}>
                <span className="dv-mention__title">{h.title}</span>
                {h.category && <span className="dv-mention__cat">{h.category}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
